import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import toast from 'react-hot-toast';
import { CreditCostNotice } from '../credits/CreditCostNotice';
import { formatCredits } from '../../hooks/useCreditPricing';
import { useCredits } from '../../hooks/useCredits';
import { VoiceEmotionEditor, countVoiceTags } from './VoiceEmotionEditor';

// =============================================================================
// Clone de Voz (Fish Audio) no Chat CRM.
//   VoiceCloneManagerModal — menu "Clone de Voz": lista/cria/remove as vozes
//                            clonadas do tenant.
//   VoiceMessageModal      — no envio: transforma o texto digitado em áudio na
//                            voz escolhida, o usuário ouve e decide se envia.
// =============================================================================

export type VoiceClone = {
  id: string;
  name: string;
  description?: string | null;
  status: 'pending' | 'ready' | 'error' | string;
  errorMessage?: string | null;
  previewUrl?: string | null;
  sampleUrl?: string | null;
  sampleSeconds?: number | null;
  isDefault: boolean;
  ready: boolean;
  createdAt: string;
};

export type GeneratedVoiceAudio = {
  url: string;
  filename?: string;
  contentType: string;
  type: 'audio';
  voiceCloneId: string;
  voiceName: string;
  credits: number;
  chars: number;
  bytes: number;
};

const MAX_SAMPLE_SECONDS = 60;
const MIN_SAMPLE_SECONDS = 8;


// Fechar-ao-clicar-no-fundo que sobrevive à seleção de texto.
//
// Arrastar para selecionar dentro do modal costuma terminar com o mouseup fora
// do conteúdo (principalmente selecionando da direita para a esquerda). O
// `click` resultante é despachado no ancestral comum do mousedown e do mouseup
// — o próprio overlay —, então o stopPropagation do conteúdo não roda e o
// modal fechava no meio da seleção. Só fecha se o gesto COMEÇOU e TERMINOU no
// fundo.
function useOverlayDismiss(onDismiss: () => void, enabled = true) {
  const startedOnOverlay = useRef(false);
  return {
    onMouseDown: (event: ReactMouseEvent) => {
      startedOnOverlay.current = event.target === event.currentTarget;
    },
    onClick: (event: ReactMouseEvent) => {
      if (!enabled) return;
      if (event.target !== event.currentTarget) return;
      if (!startedOnOverlay.current) return;
      startedOnOverlay.current = false;
      onDismiss();
    },
  };
}

const authHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

async function voiceRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`/api/crm-chat/voice-clones${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(),
      ...((options.headers as Record<string, string>) || {}),
    } as HeadersInit,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.message || data.error || `HTTP ${response.status}`);
  return data.data as T;
}

export async function fetchVoiceClones(): Promise<VoiceClone[]> {
  return voiceRequest<VoiceClone[]>('');
}

// ─────────────────────────────────────────────────────────────────────────────
// Gravador de amostra (<= 60s). Corta sozinho no limite para o usuário não
// gravar 3 minutos e só descobrir no erro do backend.
// ─────────────────────────────────────────────────────────────────────────────
function useSampleRecorder(onFinish: (blob: Blob) => void) {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);

  const stop = useCallback(() => {
    if (timerRef.current) { window.clearInterval(timerRef.current); timerRef.current = null; }
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') recorder.stop();
    recorder?.stream.getTracks().forEach((track) => track.stop());
    recorderRef.current = null;
    setRecording(false);
  }, []);

  const start = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (event) => { if (event.data.size) chunksRef.current.push(event.data); };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        if (blob.size) onFinish(blob);
      };
      recorder.start();
      recorderRef.current = recorder;
      setSeconds(0);
      setRecording(true);
      timerRef.current = window.setInterval(() => {
        setSeconds((value) => {
          const next = value + 1;
          if (next >= MAX_SAMPLE_SECONDS) stop();
          return next;
        });
      }, 1000);
    } catch {
      toast.error('Não foi possível acessar o microfone.');
    }
  }, [onFinish, stop]);

  useEffect(() => () => { if (timerRef.current) window.clearInterval(timerRef.current); }, []);

  return { recording, seconds, start, stop };
}

// ─────────────────────────────────────────────────────────────────────────────
// Menu "Clone de Voz"
// ─────────────────────────────────────────────────────────────────────────────
export function VoiceCloneManagerModal({ open, onClose, onChanged }: { open: boolean; onClose: () => void; onChanged?: (items: VoiceClone[]) => void }) {
  const [items, setItems] = useState<VoiceClone[]>([]);
  const [available, setAvailable] = useState(true);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [sample, setSample] = useState<{ blob: Blob; filename: string; url: string } | null>(null);
  const { refetch: refetchCredits } = useCredits();

  const setSampleFromBlob = useCallback((blob: Blob, filename: string) => {
    setSample((current) => {
      if (current) URL.revokeObjectURL(current.url);
      return { blob, filename, url: URL.createObjectURL(blob) };
    });
  }, []);

  const recorder = useSampleRecorder(useCallback((blob: Blob) => setSampleFromBlob(blob, 'gravacao.webm'), [setSampleFromBlob]));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/crm-chat/voice-clones', { headers: authHeaders() as HeadersInit });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'Erro ao carregar vozes');
      setItems(data.data || []);
      setAvailable(data.available !== false);
      onChanged?.(data.data || []);
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setLoading(false);
    }
  }, [onChanged]);

  useEffect(() => { if (open) load(); }, [open, load]);
  useEffect(() => () => { if (sample) URL.revokeObjectURL(sample.url); }, [sample]);
  const managerDismiss = useOverlayDismiss(onClose, !creating);

  if (!open) return null;

  const submit = async () => {
    if (!name.trim()) { toast.error('Dê um nome para a voz.'); return; }
    if (!sample) { toast.error('Grave ou envie uma amostra de áudio.'); return; }
    setCreating(true);
    const loadingToast = toast.loading('Clonando a voz no Fish Audio...');
    try {
      const formData = new FormData();
      formData.append('name', name.trim());
      if (description.trim()) formData.append('description', description.trim());
      formData.append('sample', sample.blob, sample.filename);
      const response = await fetch('/api/crm-chat/voice-clones', {
        method: 'POST',
        headers: authHeaders() as HeadersInit,
        body: formData,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'Falha ao clonar a voz');
      toast.success('Voz clonada com sucesso.');
      setName(''); setDescription('');
      if (sample) URL.revokeObjectURL(sample.url);
      setSample(null);
      refetchCredits?.();
      await load();
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      toast.dismiss(loadingToast);
      setCreating(false);
    }
  };

  const remove = async (item: VoiceClone) => {
    if (!window.confirm(`Excluir a voz "${item.name}"? Ela também será removida do Fish Audio.`)) return;
    try {
      await voiceRequest(`/${item.id}`, { method: 'DELETE' });
      toast.success('Voz removida.');
      await load();
    } catch (error) {
      toast.error((error as Error).message);
    }
  };

  const makeDefault = async (item: VoiceClone) => {
    try {
      await voiceRequest(`/${item.id}/default`, { method: 'POST' });
      await load();
    } catch (error) {
      toast.error((error as Error).message);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4" {...managerDismiss}>
      <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="text-base font-semibold text-slate-900">🎙️ Clone de Voz</h2>
            <p className="text-xs text-slate-500">Vozes clonadas desta empresa, usadas para enviar áudios no Chat CRM.</p>
          </div>
          <button onClick={onClose} className="rounded-md border border-slate-200 px-2 py-1 text-sm text-slate-600 hover:bg-slate-50">x</button>
        </div>

        {!available && (
          <div className="mx-5 mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            Clonagem indisponível: a chave <code>FISH_AUDIO_API_KEY</code> não está configurada na stack.
          </div>
        )}

        {/* Nova voz */}
        <div className="border-b border-slate-200 px-5 py-4">
          <h3 className="mb-3 text-sm font-semibold text-slate-800">Clonar uma nova voz</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Nome da voz (ex.: Wendell — comercial)"
              className="rounded-md border border-slate-300 px-3 py-2 text-sm"
            />
            <input
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="Descrição (opcional)"
              className="rounded-md border border-slate-300 px-3 py-2 text-sm"
            />
          </div>

          <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
            <p className="text-xs text-slate-600">
              Envie <strong>uma amostra limpa de {MIN_SAMPLE_SECONDS} a {MAX_SAMPLE_SECONDS} segundos</strong> —
              uma pessoa só falando, sem música nem ruído de fundo. É a clonagem rápida: quanto mais limpa a amostra, mais fiel a voz.
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <button
                onClick={recorder.recording ? recorder.stop : recorder.start}
                disabled={creating}
                className={`inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium disabled:opacity-50 ${recorder.recording ? 'border-red-300 bg-red-50 text-red-700' : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-100'}`}
              >
                {recorder.recording ? `⏹ Parar (${recorder.seconds}s / ${MAX_SAMPLE_SECONDS}s)` : '🎤 Gravar amostra'}
              </button>
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-100">
                📁 Enviar arquivo
                <input
                  type="file"
                  accept="audio/*"
                  className="hidden"
                  disabled={creating}
                  onChange={(event) => {
                    const file = event.target.files?.[0];
                    event.currentTarget.value = '';
                    if (file) setSampleFromBlob(file, file.name);
                  }}
                />
              </label>
              {sample && <span className="truncate text-xs text-slate-500">{sample.filename}</span>}
            </div>
            {sample && <audio src={sample.url} controls className="mt-3 h-9 w-full max-w-md" />}
          </div>

          {/* A Fish não tarifa a clonagem rápida, então o preço padrão é 0 e o
              aviso não renderiza. Se o superadmin ligar a cobrança no painel de
              preços, ele aparece aqui automaticamente. */}
          <CreditCostNotice className="mt-3" items={[{ featureKey: 'voice_clone', qty: 1 }]} />

          <div className="mt-3 flex justify-end">
            <button
              onClick={submit}
              disabled={creating || !available || !name.trim() || !sample}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {creating ? 'Clonando...' : 'Clonar voz'}
            </button>
          </div>
        </div>

        {/* Lista */}
        <div className="px-5 py-4">
          <h3 className="mb-3 text-sm font-semibold text-slate-800">Vozes desta empresa</h3>
          {loading && <p className="text-sm text-slate-500">Carregando...</p>}
          {!loading && items.length === 0 && <p className="text-sm text-slate-500">Nenhuma voz clonada ainda.</p>}
          <ul className="space-y-2">
            {items.map((item) => (
              <li key={item.id} className="rounded-lg border border-slate-200 px-3 py-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="flex items-center gap-2 text-sm font-medium text-slate-900">
                      {item.name}
                      {item.isDefault && <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700">padrão</span>}
                      {item.status === 'error' && <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-700">erro</span>}
                      {item.status === 'pending' && <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">processando</span>}
                    </p>
                    {item.description && <p className="mt-0.5 truncate text-xs text-slate-500">{item.description}</p>}
                    {item.status === 'error' && item.errorMessage && <p className="mt-1 text-xs text-red-600">{item.errorMessage}</p>}
                    {item.previewUrl && <audio src={item.previewUrl} controls className="mt-2 h-8 w-full max-w-sm" />}
                  </div>
                  <div className="flex shrink-0 gap-2">
                    {item.ready && !item.isDefault && (
                      <button onClick={() => makeDefault(item)} className="rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:bg-slate-50">Tornar padrão</button>
                    )}
                    <button onClick={() => remove(item)} className="rounded-md border border-red-200 px-2 py-1 text-xs text-red-600 hover:bg-red-50">Excluir</button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Modal de envio como voz clonada
// ─────────────────────────────────────────────────────────────────────────────
export function VoiceMessageModal({
  open,
  initialText,
  voices,
  onClose,
  onSend,
  onManageVoices,
}: {
  open: boolean;
  initialText: string;
  voices: VoiceClone[];
  onClose: () => void;
  onSend: (audio: GeneratedVoiceAudio) => Promise<void> | void;
  onManageVoices: () => void;
}) {
  const readyVoices = useMemo(() => voices.filter((voice) => voice.ready), [voices]);
  const [voiceId, setVoiceId] = useState('');
  const [text, setText] = useState(initialText);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [audio, setAudio] = useState<GeneratedVoiceAudio | null>(null);
  const { refetch: refetchCredits } = useCredits();
  const tagCount = countVoiceTags(text);

  // Reset SÓ na abertura. Não pode depender de initialText/readyVoices: uma
  // mudança nelas com o modal aberto descartaria um áudio já gerado e cobrado.
  const latest = useRef({ initialText, readyVoices });
  latest.current = { initialText, readyVoices };
  useEffect(() => {
    if (!open) return;
    const { initialText: startText, readyVoices: available } = latest.current;
    setText(startText);
    setAudio(null);
    setVoiceId((current) => current || available.find((voice) => voice.isDefault)?.id || available[0]?.id || '');
  }, [open]);

  // O custo é medido sobre o texto DEPOIS das regras fonéticas PT-BR ("15 cm"
  // vira "15 centimetru", que é maior). Estimar no cliente contando os bytes do
  // texto cru mostraria menos do que o cobrado, então quem calcula é o backend
  // — a mesma função que a cobrança usa.
  const [estimate, setEstimate] = useState<{ bytes: number; qty: number; credits: number } | null>(null);
  useEffect(() => {
    if (!open || !text.trim()) { setEstimate(null); return; }
    let alive = true;
    const timer = window.setTimeout(() => {
      voiceRequest<{ bytes: number; qty: number; credits: number }>('/estimate', {
        method: 'POST',
        body: JSON.stringify({ text }),
      })
        .then((data) => { if (alive) setEstimate(data); })
        .catch(() => { if (alive) setEstimate(null); });
    }, 350);
    return () => { alive = false; window.clearTimeout(timer); };
  }, [open, text]);

  const voiceDismiss = useOverlayDismiss(onClose, !generating && !sending);

  if (!open) return null;

  const qty = estimate?.qty ?? 0;

  const generate = async () => {
    if (!voiceId) { toast.error('Escolha uma voz.'); return; }
    if (!text.trim()) { toast.error('Digite o texto do áudio.'); return; }
    setGenerating(true);
    setAudio(null);
    try {
      const result = await voiceRequest<GeneratedVoiceAudio>(`/${voiceId}/synthesize`, {
        method: 'POST',
        body: JSON.stringify({ text }),
      });
      setAudio(result);
      refetchCredits?.();
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setGenerating(false);
    }
  };

  const send = async () => {
    if (!audio) return;
    setSending(true);
    try {
      await onSend(audio);
      onClose();
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4" {...voiceDismiss}>
      <div className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="text-base font-semibold text-slate-900">🔊 Enviar como voz clonada</h2>
            <p className="text-xs text-slate-500">Gere o áudio, ouça e só então envie.</p>
          </div>
          <button onClick={onClose} disabled={generating || sending} className="rounded-md border border-slate-200 px-2 py-1 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-40">x</button>
        </div>

        <div className="space-y-3 px-5 py-4">
          {readyVoices.length === 0 ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 text-xs text-amber-800">
              Nenhuma voz clonada disponível.{' '}
              <button onClick={onManageVoices} className="font-semibold underline">Clonar uma voz agora</button>
            </div>
          ) : (
            <label className="block">
              <span className="text-xs font-medium text-slate-600">Voz</span>
              <select
                value={voiceId}
                onChange={(event) => { setVoiceId(event.target.value); setAudio(null); }}
                disabled={generating || sending}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              >
                {readyVoices.map((voice) => (
                  <option key={voice.id} value={voice.id}>{voice.name}{voice.isDefault ? ' (padrão)' : ''}</option>
                ))}
              </select>
            </label>
          )}

          <VoiceEmotionEditor
            value={text}
            onChange={(next) => { setText(next); setAudio(null); }}
            disabled={generating || sending}
            footer={(
              <span className="mt-1 block text-[11px] text-slate-500">
                {text.length} caracteres{estimate ? ` · ${estimate.bytes} bytes faturáveis` : ''}
                {tagCount > 0 ? ` · ${tagCount} marcação${tagCount > 1 ? 'ões' : ''} de voz` : ''}
              </span>
            )}
          />

          {qty > 0 && (
            <CreditCostNotice
              items={[{ featureKey: 'voice_tts_1k', qty, label: 'mil caracteres' }]}
              note="Cobrado ao gerar o áudio; ouvir e reenviar o mesmo áudio não cobra de novo."
            />
          )}

          {audio && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-3">
              <p className="text-xs font-semibold text-emerald-800">
                Áudio pronto na voz "{audio.voiceName}" — {formatCredits(audio.credits)} crédito{audio.credits !== 1 ? 's' : ''} consumido{audio.credits !== 1 ? 's' : ''}
              </p>
              <audio src={audio.url} controls autoPlay className="mt-2 h-9 w-full" />
            </div>
          )}
        </div>

        <div className="flex flex-wrap justify-end gap-2 border-t border-slate-200 px-5 py-4">
          <button onClick={onClose} disabled={generating || sending} className="rounded-md border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-40">
            Cancelar
          </button>
          <button
            onClick={generate}
            disabled={generating || sending || !voiceId || !text.trim()}
            className="rounded-md border border-blue-300 bg-blue-50 px-4 py-2 text-sm font-medium text-blue-700 hover:bg-blue-100 disabled:opacity-40"
          >
            {generating ? 'Gerando áudio...' : audio ? 'Gerar novamente' : 'Gerar áudio'}
          </button>
          <button
            onClick={send}
            disabled={!audio || generating || sending}
            className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-40"
          >
            {sending ? 'Enviando...' : 'Enviar áudio'}
          </button>
        </div>
      </div>
    </div>
  );
}
